import { VaButton } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import PropTypes from 'prop-types';
import React from 'react';

import { OPTION_TYPES } from '../../utils/directLineActivity';
import { getIconOptionLabel } from '../../utils/iconOptionLabels';

/**
 * @typedef {Object} ChatMessageOptionsProps
 * @property {string} messageId
 * @property {string[]} options
 * @property {'icon'|'text'} [optionType]
 * @property {boolean} [areIconOptionsDisabled]
 * @property {(value: string, optionType?: string) => void} [onOptionSelect]
 */

/**
 * Renders assistant follow-up option buttons.
 * @component
 * @param {ChatMessageOptionsProps} props
 * @returns {JSX.Element | null}
 */
export default function ChatMessageOptions({
  areIconOptionsDisabled = false,
  messageId,
  onOptionSelect,
  options,
  optionType = OPTION_TYPES.TEXT,
}) {
  if (!Array.isArray(options) || options.length === 0) {
    return null;
  }

  if (optionType === OPTION_TYPES.ICON) {
    return (
      <div className="va-chatbot-icon-options">
        {options.map(option => {
          const srText = getIconOptionLabel(option);

          return (
            // Native button is intentional here because va-button only supports
            // text labels and cannot render the required va-icon-only control.
            // eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component
            <button
              aria-label={srText}
              className="va-chatbot-icon-options__button"
              data-testid="chat-message-icon-option"
              disabled={areIconOptionsDisabled}
              key={`${messageId}-option-${option}`}
              onClick={() => onOptionSelect?.(option, OPTION_TYPES.ICON)}
              type="button"
            >
              <va-icon icon={option} size="3" srtext={srText} />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="vads-u-margin-top--0p5 vads-u-display--flex vads-u-flex-direction--column">
      {options.map((option, index) => (
        <VaButton
          className={index > 0 ? 'vads-u-margin-top--0p5' : ''}
          data-testid="chat-message-option"
          fullWidth
          key={`${messageId}-option-${option}`}
          onClick={() => onOptionSelect?.(option)}
          text={option}
        />
      ))}
    </div>
  );
}

ChatMessageOptions.propTypes = {
  messageId: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  areIconOptionsDisabled: PropTypes.bool,
  optionType: PropTypes.oneOf(Object.values(OPTION_TYPES)),
  onOptionSelect: PropTypes.func,
};
